import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { jobsEndpoint } from '../constants';

const CandidateDetails = () => {
  const [applicant, setApplicant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchApplicantDetails = async () => {
      try {
        const response = await fetch(`${jobsEndpoint}applicant/${id}/`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          }
        });
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Candidate not found');
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setApplicant(data);
      } catch (error) {
        console.error('Failed to fetch candidate details:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchApplicantDetails();
    }
  }, [id, token]);

  const getReviewColor = (review) => {
    const reviewColors = {
      'PENDING': 'bg-yellow-100 text-yellow-800',
      'IN_PROGRESS': 'bg-blue-100 text-blue-800',
      'COMPLETED': 'bg-green-100 text-green-800',
      'REJECTED': 'bg-red-100 text-red-800'
    };
    return reviewColors[review] || 'bg-gray-100 text-gray-800';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRatingColor = (rating) => {
    const ratingColors = {
      'EXCELLENT': 'bg-green-100 text-green-800',
      'GOOD': 'bg-blue-100 text-blue-800',
      'AVERAGE': 'bg-yellow-100 text-yellow-800',
      'BELOW_AVERAGE': 'bg-orange-100 text-orange-800',
      'POOR': 'bg-red-100 text-red-800'
    };
    return ratingColors[rating] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading candidate details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Candidate</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <div className="space-x-4">
            <button 
              onClick={() => window.location.reload()} 
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
            <button 
              onClick={() => navigate('/candidates')} 
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
            >
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!applicant) {
    return null;
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📋' },
    { id: 'evaluation', name: 'AI Evaluation', icon: '🤖' },
    { id: 'documents', name: 'Documents', icon: '📄' },
    { id: 'application', name: 'Application Details', icon: '📝' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/candidates')}
                className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Candidates
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Candidate #{applicant.id}
                </h1>
                <p className="text-gray-600">
                  {applicant.user?.first_name && applicant.user?.last_name 
                    ? `${applicant.user.first_name} ${applicant.user.last_name}` 
                    : applicant.user?.email || 'No name available'
                  }
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getReviewColor(applicant.ai_review || 'PENDING')}`}>
                {applicant.ai_review || 'PENDING'}
              </span>
              {applicant.ai_score && (
                <div className="text-right">
                  <div className={`text-2xl font-bold ${getScoreColor(applicant.ai_score)}`}>
                    {applicant.ai_score}
                  </div>
                  <div className="text-xs text-gray-500">AI Score</div>
                </div>
              )}
            </div>
          </div>
          
          {/* Tabs */}
          <div className="mt-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Basic Information */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Candidate Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Candidate ID</label>
                    <p className="text-gray-900">#{applicant.id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Full Name</label>
                    <p className="text-gray-900">
                      {applicant.user?.first_name && applicant.user?.last_name 
                        ? `${applicant.user.first_name} ${applicant.user.last_name}` 
                        : 'Not provided'
                      }
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-gray-900">{applicant.user?.email || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Job Position</label>
                    <p className="text-gray-900">{applicant.job?.title || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Application Date</label>
                    <p className="text-gray-900">{new Date(applicant.date_published).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Updated</label>
                    <p className="text-gray-900">{new Date(applicant.date_updated).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* User Account Details */}
              {applicant.user && (
                <div className="bg-white rounded-lg shadow-sm border p-6 mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Username</label>
                      <p className="text-gray-900">{applicant.user.username || 'Not provided'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Account Status</label>
                      <p className="text-gray-900">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          applicant.user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {applicant.user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Account Type</label>
                      <p className="text-gray-900">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          applicant.user.is_staff ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {applicant.user.is_staff ? 'Staff' : 'Regular User'}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Join Date</label>
                      <p className="text-gray-900">
                        {applicant.user.date_joined ? new Date(applicant.user.date_joined).toLocaleDateString() : 'Not available'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quick AI Summary */}
            <div>
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Summary</h3>
                {applicant.ai_score ? (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">AI Score</label>
                      <p className={`text-2xl font-bold ${getScoreColor(applicant.ai_score)}`}>
                        {applicant.ai_score}/100
                      </p>
                    </div>
                    {applicant.ai_rating && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Rating</label>
                        <p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRatingColor(applicant.ai_rating)}`}>
                            {applicant.ai_rating.replace('_', ' ')}
                          </span>
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium text-gray-500">Review Status</label>
                      <p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getReviewColor(applicant.ai_review || 'PENDING')}`}>
                          {applicant.ai_review || 'PENDING'}
                        </span>
                      </p>
                    </div>
                    {applicant.ai_recommendation && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Quick Recommendation</label>
                        <p className="text-gray-900 text-sm">{applicant.ai_recommendation.substring(0, 100)}...</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500">No AI evaluation available</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'evaluation' && (
          <div className="max-w-4xl">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">AI Evaluation Report</h3>
              
              {applicant.ai_score ? (
                <div className="space-y-6">
                  {/* Score and Rating */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-gray-50 rounded-lg">
                      <div className={`text-4xl font-bold mb-2 ${getScoreColor(applicant.ai_score)}`}>
                        {applicant.ai_score}
                      </div>
                      <div className="text-gray-600">Overall Score</div>
                    </div>
                    {applicant.ai_rating && (
                      <div className="text-center p-6 bg-gray-50 rounded-lg">
                        <div className="mb-2">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRatingColor(applicant.ai_rating)}`}>
                            {applicant.ai_rating.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="text-gray-600">AI Rating</div>
                      </div>
                    )}
                    <div className="text-center p-6 bg-gray-50 rounded-lg">
                      <div className="mb-2">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getReviewColor(applicant.ai_review || 'PENDING')}`}>
                          {applicant.ai_review || 'PENDING'}
                        </span>
                      </div>
                      <div className="text-gray-600">Review Status</div>
                    </div>
                  </div>

                  {/* Recommendation */}
                  {applicant.ai_recommendation && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">💡 Recommendation</h4>
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                        <p className="text-gray-700">{applicant.ai_recommendation}</p>
                      </div>
                    </div>
                  )}

                  {/* Advantages */}
                  {applicant.ai_advantages && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">✅ Strengths</h4>
                      <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                        <p className="text-gray-700 whitespace-pre-line">{applicant.ai_advantages}</p>
                      </div>
                    </div>
                  )}

                  {/* Criticism */}
                  {applicant.ai_criticism && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">⚠️ Areas for Improvement</h4>
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                        <p className="text-gray-700 whitespace-pre-line">{applicant.ai_criticism}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-4xl mb-4">🤖</div>
                  <h4 className="text-lg font-semibold text-gray-700 mb-2">No AI Evaluation Available</h4>
                  <p className="text-gray-500">This candidate hasn't been evaluated by AI yet.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="max-w-4xl">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Documents</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* CV Document */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <svg className="w-8 h-8 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <h4 className="font-medium text-gray-900">CV/Resume</h4>
                      <p className="text-sm text-gray-500">Curriculum Vitae</p>
                    </div>
                  </div>
                  {applicant.cv ? (
                    <div className="flex space-x-2">
                      <a 
                        href={applicant.cv}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </a>
                      <a 
                        href={applicant.cv}
                        download
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download
                      </a>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No CV uploaded</p>
                  )}
                </div>

                {/* Cover Letter */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <svg className="w-8 h-8 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div>
                      <h4 className="font-medium text-gray-900">Cover Letter</h4>
                      <p className="text-sm text-gray-500">Motivation Letter</p>
                    </div>
                  </div>
                  {applicant.coverletter ? (
                    <div className="flex space-x-2">
                      <a 
                        href={applicant.coverletter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </a>
                      <a 
                        href={applicant.coverletter}
                        download
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download
                      </a>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No cover letter uploaded</p>
                  )}
                </div>

                {/* Extra Form File */}
                {applicant.extra_form_file && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center mb-3">
                      <svg className="w-8 h-8 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <h4 className="font-medium text-gray-900">Additional Document</h4>
                        <p className="text-sm text-gray-500">Extra form file</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <a 
                        href={applicant.extra_form_file}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </a>
                      <a 
                        href={applicant.extra_form_file}
                        download
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'application' && (
          <div className="max-w-4xl">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Application Details</h3>
              
              {/* Application Text */}
              {applicant.application && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">📝 Application Statement</h4>
                  <div className="bg-gray-50 border-l-4 border-blue-400 p-4 rounded">
                    <p className="text-gray-700 whitespace-pre-line">{applicant.application}</p>
                  </div>
                </div>
              )}

              {/* Form Answers */}
              {applicant.form_answers && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">📋 Form Responses</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    {typeof applicant.form_answers === 'object' ? (
                      <div className="space-y-4">
                        {Object.entries(applicant.form_answers).map(([question, answer], index) => (
                          <div key={index} className="border-b border-gray-200 pb-3 last:border-b-0">
                            <dt className="text-sm font-medium text-gray-600 mb-1">
                              {question.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </dt>
                            <dd className="text-gray-900">{String(answer)}</dd>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-700">{applicant.form_answers}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Job Information */}
              {applicant.job && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">💼 Job Information</h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Job Title</label>
                        <p className="text-gray-900 font-medium">{applicant.job.title}</p>
                      </div>
                      {applicant.job.department && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Department</label>
                          <p className="text-gray-900">{applicant.job.department}</p>
                        </div>
                      )}
                      {applicant.job.location && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Location</label>
                          <p className="text-gray-900">{applicant.job.location}</p>
                        </div>
                      )}
                      {applicant.job.employment_type && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Employment Type</label>
                          <p className="text-gray-900">{applicant.job.employment_type}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">⏱️ Application Timeline</h4>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Application Submitted</p>
                      <p className="text-sm text-gray-500">{new Date(applicant.date_published).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Last Updated</p>
                      <p className="text-sm text-gray-500">{new Date(applicant.date_updated).toLocaleString()}</p>
                    </div>
                  </div>
                  {applicant.ai_review && applicant.ai_review !== 'PENDING' && (
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">AI Review Status</p>
                        <p className="text-sm text-gray-500">{applicant.ai_review}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* No Data Message */}
              {!applicant.application && !applicant.form_answers && (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-4xl mb-4">📝</div>
                  <h4 className="text-lg font-semibold text-gray-700 mb-2">No Application Details</h4>
                  <p className="text-gray-500">No additional application information is available for this candidate.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6">
        <div className="flex flex-col space-y-2">
          <button className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          {(!applicant.ai_score || applicant.ai_review === 'PENDING') && (
            <button className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-full shadow-lg transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CandidateDetails;